/*
 * New HP Stories — fetches Judge.me reviews and hydrates them into the markup
 * rendered by sections/new-hp-stories.liquid.
 *
 * Where the reviews come from:
 * Judge.me's shop metafield cache holds one page of the all-reviews widget, and
 * that page is capped at 25 reviews, so the section fetches from the widget API
 * instead: https://api.judge.me/api/v1/widgets/all_reviews_page
 * The endpoint is CORS-enabled, takes the public token, and returns the same
 * `jdgm-rev` markup the metafields held — so the cards, the CSS, the Embla
 * slider and the product-image hydration below are all unchanged by the switch.
 *
 * Lives in an asset rather than inline in the section so that:
 *  - the work is done once per page, not once per section instance,
 *  - the review fetch and the product-image cache are shared across instances,
 *  - the file is parsed once and cached by the browser,
 *  - a `shopify:section:load` listener survives a theme-editor re-render
 *    (section markup is swapped in as HTML, so inline scripts are unreliable).
 */
(function () {
  if (window.__hpJdgmStaticInit) return;
  window.__hpJdgmStaticInit = true;

  var CONTAINER_SELECTOR = '.hp-jdgm-static[data-hp-stories-app]';
  var LAZY_SRC_ATTRS = ['data-src', 'data-lazy', 'data-original', 'data-echo'];

  var API_URL = 'https://api.judge.me/api/v1/widgets/all_reviews_page';

  // The endpoint silently caps per_page at 25 — asking for 100 still returns 25
  // — so the page size is fixed and the limit is reached by paging instead.
  var PER_PAGE = 25;

  // Hard ceiling on requests per page load. Without it, a shop whose reviews are
  // mostly below the rating filter would page until it ran out of reviews.
  var MAX_PAGES = 8;

  // A hung response must not leave the skeleton shimmering forever, so every
  // request is given a deadline of its own.
  var REQUEST_TIMEOUT = 9000;

  // One retry, for a dropped connection or a shed load response. Long enough to
  // ride out a blip, short enough that it is invisible next to the fetch itself.
  var RETRY_DELAY = 500;

  // How close the section has to be before its reviews are worth fetching.
  var ROOT_MARGIN = '600px';

  // Part of the storage key. Bump it when the shape of the cached payload
  // changes, so old payloads are ignored instead of being mis-read.
  var CACHE_VERSION = 'v3';

  // In-flight and completed fetches, keyed by cache key, so two instances of the
  // section on one page share a single request rather than racing each other.
  var reviewRequests = {};

  // Progressive-render callbacks per cache key. Sharing one request across
  // instances means the pages arrive once but every instance still has to be
  // told about each one.
  var reviewProgress = {};

  var sectionObserver = null;

  // Judge.me caches only the product handle in the review link, so the product
  // image is fetched per unique handle and shared across reviews *and* sections.
  var productRequests = {};
  var productImageObserver = null;

  /* ---------------------------------------------------------------- settings */

  function readSettings(container) {
    var data = container.dataset;
    var limit = parseInt(data.reviewLimit, 10);

    return {
      shopDomain: data.shopDomain || (window.Shopify && window.Shopify.shop) || '',
      apiToken: data.apiToken || '',
      minRating: data.minRating || 'any',
      limit: isNaN(limit) || limit < 1 ? 12 : limit,
      showReviewImage: data.showReviewImage === 'true',
      enableSlider: data.enableSlider === 'true',
    };
  }

  // 'any' needs no per-review check, so it is a null rather than a score list.
  function allowedScores(minRating) {
    if (minRating === '5') return ['5'];
    if (minRating === '4') return ['4', '5'];
    return null;
  }

  /* ----------------------------------------------------------------- caching */

  // sessionStorage rather than localStorage: reviews change often enough that a
  // long-lived copy would go stale, and a session is a natural expiry that needs
  // no TTL bookkeeping and no clock to trust. The cost is one refetch per new
  // session. To switch to localStorage, change this to window.localStorage and
  // check payload.ts against a max age in readCache.
  function storage() {
    try {
      return window.sessionStorage;
    } catch (error) {
      // Safari in private mode and "block cookies" both throw on access alone.
      return null;
    }
  }

  // The key carries every input that changes what is stored, so switching the
  // rating filter or the limit reads a different entry instead of a stale one.
  function cacheKey(settings) {
    return [
      'hp-jdgm',
      CACHE_VERSION,
      settings.shopDomain,
      settings.minRating,
      settings.limit,
    ].join(':');
  }

  function readCache(key) {
    var store = storage();
    if (!store) return null;

    try {
      var raw = store.getItem(key);
      if (!raw) return null;

      var payload = JSON.parse(raw);
      // A payload written by an older version of this file, or half-written by
      // a quota error, is treated as a miss rather than trusted.
      if (!payload || payload.v !== CACHE_VERSION || !Array.isArray(payload.reviews)) {
        return null;
      }
      return payload;
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, payload) {
    var store = storage();
    if (!store) return;

    try {
      store.setItem(key, JSON.stringify(payload));
    } catch (error) {
      // Over quota, or storage disabled. The section works without the cache,
      // so this is deliberately silent.
    }
  }

  /* ------------------------------------------------------------------ fetch */

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function attemptPage(settings, page) {
    var url =
      API_URL +
      '?api_token=' + encodeURIComponent(settings.apiToken) +
      '&shop_domain=' + encodeURIComponent(settings.shopDomain) +
      '&page=' + page +
      '&per_page=' + PER_PAGE;

    // credentials are omitted (the default for cross-origin fetch): the endpoint
    // echoes the requesting origin in Access-Control-Allow-Origin, and a
    // credentialed request against an echoed origin is rejected by the browser.
    var options = { method: 'GET', mode: 'cors', credentials: 'omit' };
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = null;

    if (controller) options.signal = controller.signal;

    function expiredError() {
      var expired = new Error('Judge.me did not respond in time.');
      expired.timeout = true;
      return expired;
    }

    // The deadline rejects on its own rather than waiting for the abort to
    // surface. Aborting still matters — it frees the connection — but it is the
    // optimisation, not the mechanism, so the timeout also holds on browsers
    // with no AbortController and if a request never honours the signal.
    var deadline = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(expiredError());
      }, REQUEST_TIMEOUT);
    });

    var request = fetch(url, options).then(
      function (response) {
        if (!response.ok) {
          var failed = new Error('Judge.me responded ' + response.status);
          // Kept on the error so isRetryable can tell a shed-load response
          // apart from a request that was simply wrong.
          failed.status = response.status;
          throw failed;
        }
        return response.json();
      },
      function (error) {
        if (error && error.name === 'AbortError') throw expiredError();
        throw error;
      }
    );

    return Promise.race([request, deadline]).then(
      function (data) {
        clearTimeout(timer);
        return data;
      },
      function (error) {
        clearTimeout(timer);
        throw error;
      }
    );
  }

  // Worth a second go: a dropped connection, a rate limit, or a server-side
  // error. Not worth it: a timeout (retrying doubles the worst case the visitor
  // waits) or a 4xx that will fail identically the second time.
  function isRetryable(error) {
    if (!error || error.timeout) return false;
    if (typeof error.status === 'number') return error.status === 429 || error.status >= 500;
    return true;
  }

  function requestPage(settings, page, isRetry) {
    return attemptPage(settings, page).catch(function (error) {
      if (isRetry || !isRetryable(error)) throw error;
      return delay(RETRY_DELAY).then(function () {
        return requestPage(settings, page, true);
      });
    });
  }

  // Judge.me returns rendered HTML, not review objects, so the reviews are read
  // back out of an inert parsed document. Inert matters: nothing in it loads or
  // executes until the nodes are adopted into the real page.
  function parseReviewNodes(html) {
    if (!html) return [];
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    return Array.prototype.slice.call(parsed.querySelectorAll('.jdgm-rev'));
  }

  function scoreOf(node) {
    var rated = node.querySelector('[data-score]');
    return rated ? rated.getAttribute('data-score') : '';
  }

  // Walks pages until enough reviews pass the filter, the shop runs out, or the
  // page ceiling is hit — whichever comes first. onProgress is called with a
  // snapshot after every page that did not finish the job, so the cards already
  // in hand can go on screen while the rest are still in flight.
  function collectReviews(settings, onProgress) {
    var allowed = allowedScores(settings.minRating);
    var seenIds = {};
    var kept = [];
    var stats = { fetched: 0, pages: 0, passed: 0 };

    function snapshot(extra) {
      var payload = {
        v: CACHE_VERSION,
        ts: Date.now(),
        reviews: kept.slice(),
        stats: { fetched: stats.fetched, pages: stats.pages, passed: stats.passed },
      };
      if (extra) payload.incomplete = true;
      return payload;
    }

    function step(page) {
      return requestPage(settings, page).then(function (data) {
        stats.pages = page;

        var nodes = parseReviewNodes(data.all_reviews);
        stats.fetched += nodes.length;

        nodes.forEach(function (node) {
          if (kept.length >= settings.limit) return;

          var id = node.getAttribute('data-review-id') || '';
          if (id && seenIds[id]) return;
          if (allowed && allowed.indexOf(scoreOf(node)) === -1) return;

          if (id) seenIds[id] = true;
          kept.push(node.outerHTML);
        });

        stats.passed = kept.length;

        var lastPage = nodes.length < PER_PAGE;
        if (kept.length >= settings.limit || lastPage || page >= MAX_PAGES) {
          return snapshot();
        }

        // Not done, but there is something to show — hand it over before
        // spending another round trip on the rest.
        if (onProgress && kept.length) onProgress(snapshot());

        return step(page + 1);
      });
    }

    return step(1).catch(function (error) {
      // A later page failing is not a reason to discard the pages that did
      // arrive; the caller renders these and skips the cache write.
      if (kept.length) return snapshot(true);
      throw error;
    });
  }

  // One shared promise per cache key: a cache hit resolves without a request,
  // and a miss is fetched exactly once per page load however many sections ask.
  function loadReviews(settings, onProgress) {
    var key = cacheKey(settings);

    if (onProgress) {
      reviewProgress[key] = reviewProgress[key] || [];
      reviewProgress[key].push(onProgress);
    }

    if (reviewRequests[key]) return reviewRequests[key];

    var cached = readCache(key);
    if (cached) {
      reviewRequests[key] = Promise.resolve(cached);
      return reviewRequests[key];
    }

    reviewRequests[key] = collectReviews(settings, function (partial) {
      (reviewProgress[key] || []).forEach(function (listener) {
        // One instance throwing must not stop the others being told.
        try {
          listener(partial);
        } catch (error) {
          /* ignored */
        }
      });
    }).then(function (payload) {
      // A run that gave up part way is rendered but never cached — the next
      // page view should get a clean attempt rather than a truncated list.
      if (payload.reviews.length && !payload.incomplete) writeCache(key, payload);
      return payload;
    });

    return reviewRequests[key];
  }

  /* ----------------------------------------------------------------- render */

  function buildCard(html, settings) {
    var card = document.createElement('div');
    card.className = 'hp-jdgm-static__pick';
    card.innerHTML = html;

    // The card reserves a fixed left column for the review photo, so a card with
    // no photo to show — the review has none, or the merchant turned photos off
    // — falls back to plain padding.
    if (!settings.showReviewImage || !card.querySelector('.jdgm-rev__pics')) {
      card.classList.add('hp-jdgm-static__pick--no-photo');
    }

    return card;
  }

  // Called once per page of reviews, so it appends what is new rather than
  // rebuilding the list — re-rendering everything each time would throw away
  // the images already hydrated into the cards on screen.
  function renderReviews(container, payload, settings) {
    var track = container.querySelector('[data-hp-stories-track]');
    if (!track) return;

    var rendered = parseInt(track.dataset.hpJdgmRendered || '0', 10) || 0;
    var pending = payload.reviews.slice(rendered);
    if (!pending.length) return;

    // The skeleton only has to be cleared once, by whichever batch lands first.
    if (!rendered) {
      track.innerHTML = '';
      container.removeAttribute('aria-busy');
    }

    var fragment = document.createDocumentFragment();

    pending.forEach(function (html) {
      var card = buildCard(html, settings);

      if (settings.enableSlider) {
        var slide = document.createElement('div');
        slide.className = 'embla__slide';
        slide.appendChild(card);
        fragment.appendChild(slide);
      } else {
        fragment.appendChild(card);
      }
    });

    track.appendChild(fragment);
    track.dataset.hpJdgmRendered = payload.reviews.length;

    hydrate(container);
  }

  // Sets up the stories carousel. It lives here rather than in a page-type
  // script (homepage-custom.js only loads on the homepage) so the slider works
  // on any template the section is added to. EmblaCarousel comes from
  // embla-slider-min.js, which layout/theme.liquid loads on every page.
  function initCarousel(wrapperNode) {
    if (wrapperNode.dataset.emblaInitialized === 'true') return;

    var viewportNode = wrapperNode.querySelector('.embla__viewport');
    if (!viewportNode || typeof EmblaCarousel === 'undefined') return;

    wrapperNode.dataset.emblaInitialized = 'true';

    var loopEnabled = wrapperNode.dataset.loop !== 'false';
    var emblaApi = EmblaCarousel(viewportNode, {
      loop: loopEnabled,
      align: 'start',
      slidesToScroll: 1,
    });

    var sectionRoot = wrapperNode.closest('.new-hp-stories') || wrapperNode;
    var prevButtonNode = sectionRoot.querySelector('.embla__prev');
    var nextButtonNode = sectionRoot.querySelector('.embla__next');

    function updateNavButtons() {
      if (!prevButtonNode || !nextButtonNode) return;

      if (loopEnabled && emblaApi.scrollSnapList().length > 1) {
        prevButtonNode.disabled = false;
        nextButtonNode.disabled = false;
        return;
      }

      prevButtonNode.disabled = !emblaApi.canScrollPrev();
      nextButtonNode.disabled = !emblaApi.canScrollNext();
    }

    if (prevButtonNode) prevButtonNode.addEventListener('click', function () { emblaApi.scrollPrev(); });
    if (nextButtonNode) nextButtonNode.addEventListener('click', function () { emblaApi.scrollNext(); });

    emblaApi.on('select', updateNavButtons);
    emblaApi.on('reInit', updateNavButtons);
    updateNavButtons();

    // Autoplay from the section's settings. Pauses on hover and while the tab
    // is hidden; off for visitors who prefer reduced motion. An arrow click
    // restarts the countdown so the slide doesn't jump right after it.
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (wrapperNode.dataset.autoplay !== 'true' || reducedMotion || emblaApi.scrollSnapList().length <= 1) return;

    var autoplayDelay = Number(wrapperNode.dataset.autoplayDelay) || 5000;
    var timer = null;

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    function play() {
      stop();
      timer = setInterval(function () {
        // Without loop, go back to the first slide after the last.
        if (emblaApi.canScrollNext()) emblaApi.scrollNext();
        else emblaApi.scrollTo(0);
      }, autoplayDelay);
    }

    play();
    wrapperNode.addEventListener('mouseenter', stop);
    wrapperNode.addEventListener('mouseleave', play);
    if (prevButtonNode) prevButtonNode.addEventListener('click', function () { if (timer) play(); });
    if (nextButtonNode) nextButtonNode.addEventListener('click', function () { if (timer) play(); });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else play();
    });
  }

  // Embla reads its slides once, at init, so the carousel can only be started
  // after the last page has landed. Until then the cards are already on
  // screen, just not yet scrollable.
  function finishRender(container, payload, settings) {
    renderReviews(container, payload, settings);

    var carousel = container.querySelector('.embla[data-embla-deferred]');
    if (settings.enableSlider && carousel && payload.reviews.length > 1) {
      carousel.removeAttribute('data-embla-deferred');
      initCarousel(carousel);

      var controls = container.querySelector('[data-hp-stories-controls]');
      if (controls) controls.hidden = false;
    }
  }

  // Nothing to show: hide the whole section rather than leave a skeleton
  // shimmering forever. In the theme editor it stays visible with the reason,
  // so the section does not silently vanish while someone is editing it.
  function failGracefully(container, reason) {
    // Reviews are already on screen: a later page failing is not a reason to
    // take away the ones that arrived.
    var rendered = container.querySelector('[data-hp-stories-track]');
    if (rendered && parseInt(rendered.dataset.hpJdgmRendered || '0', 10) > 0) return;

    var section = container.closest('[data-hp-stories-section]');
    var designMode = window.Shopify && window.Shopify.designMode;

    if (!designMode) {
      if (section) section.hidden = true;
      return;
    }

    var track = container.querySelector('[data-hp-stories-track]');
    if (track) track.innerHTML = '';

    var fallback = container.querySelector('[data-hp-stories-fallback]');
    if (fallback) {
      fallback.textContent = reason;
      fallback.hidden = false;
    }
    container.removeAttribute('aria-busy');
  }

  /* ----------------------------------------------- theme editor review index */

  function textFrom(card, selector) {
    var node = card.querySelector(selector);
    return node ? (node.textContent || '').trim() : '';
  }

  function renderIndex(container, payload, settings) {
    var index = container.parentNode
      ? container.parentNode.querySelector('[data-hp-stories-index]')
      : null;
    if (!index) return;

    var scratch = document.createElement('div');
    var rows = payload.reviews
      .map(function (html, position) {
        scratch.innerHTML = html;
        var author = textFrom(scratch, '.jdgm-rev__author');
        var product = textFrom(scratch, '.jdgm-rev__prod-link');
        var id = scratch.firstElementChild
          ? scratch.firstElementChild.getAttribute('data-review-id') || ''
          : '';

        return (
          '<li><strong>Review ' + (position + 1) + '</strong>' +
          (author ? ' — ' + author : '') +
          (product ? ' · ' + product.slice(0, 60) : '') +
          (id ? '<br><code>' + id + '</code>' : '') +
          '</li>'
        );
      })
      .join('');

    index.innerHTML =
      '<summary>Judge.me reviews shown here (' + payload.reviews.length + ')</summary>' +
      '<p class="hp-jdgm-index__counts">' +
      'Fetched ' + payload.stats.fetched + ' review(s) from the Judge.me API ' +
      'over ' + payload.stats.pages + ' request(s) of ' + PER_PAGE + ' → ' +
      '<strong>' + payload.stats.passed + ' passed the rating filter</strong>' +
      ' (asked for ' + settings.limit + ').' +
      (payload.stats.pages >= MAX_PAGES && payload.stats.passed < settings.limit
        ? '<br><strong>Stopped at the ' + MAX_PAGES + '-request ceiling</strong> before filling the' +
          ' limit — most reviews are below the minimum rating. Lower the rating or the limit.'
        : '') +
      '<br>Served from ' + (payload.fromCache ? 'the sessionStorage cache' : 'the network') + '.' +
      '</p>' +
      (rows ? '<ol class="hp-jdgm-index__list">' + rows + '</ol>' : '');
  }

  /* ------------------------------------------------- images (unchanged logic) */

  function realSrcOf(img) {
    for (var i = 0; i < LAZY_SRC_ATTRS.length; i++) {
      var value = img.getAttribute(LAZY_SRC_ATTRS[i]);
      if (value) return value;
    }
    return '';
  }

  // Judge.me hands review photos over as 160px thumbnails (…&width=160), but the
  // cards show them at ~300px wide and full-width on mobile, so they render
  // blurry. Its image CDN resizes on request, so swap in a srcset of real sizes.
  var REVIEW_IMAGE_WIDTHS = [320, 480, 640, 960];
  var REVIEW_IMAGE_SIZES = '(min-width: 990px) 300px, (min-width: 750px) 50vw, 100vw';

  function upscaleReviewImage(img) {
    var src = img.getAttribute('src') || '';
    if (src.indexOf('review-images.judgeme.com') === -1 || !/[?&]width=\d+/.test(src)) return;

    var srcset = REVIEW_IMAGE_WIDTHS.map(function (width) {
      return src.replace(/([?&]width=)\d+/, '$1' + width) + ' ' + width + 'w';
    }).join(', ');

    img.setAttribute('src', src.replace(/([?&]width=)\d+/, '$1' + 480));
    img.setAttribute('srcset', srcset);
    img.setAttribute('sizes', REVIEW_IMAGE_SIZES);
  }

  function resolveLazyImages(container) {
    var images = container.querySelectorAll('img');
    Array.prototype.forEach.call(images, function (img) {
      var currentSrc = img.getAttribute('src') || '';
      var isPlaceholder = !currentSrc || currentSrc.indexOf('data:image') === 0;
      var realSrc = realSrcOf(img);

      if (realSrc && isPlaceholder) {
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
        img.setAttribute('src', realSrc);
      }

      var realSrcset = img.getAttribute('data-srcset');
      if (realSrcset && !img.getAttribute('srcset')) {
        img.setAttribute('srcset', realSrcset);
      }

      upscaleReviewImage(img);

      // Judge.me's stylesheet keeps un-promoted images hidden
      img.classList.remove('jdgm-lazy', 'lazyload');
      img.classList.add('lazyloaded');
    });
  }

  function handleFromLink(link) {
    var match = String(link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function fetchProduct(handle) {
    if (!productRequests[handle]) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      productRequests[handle] = fetch(root + 'products/' + handle + '.js')
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .catch(function () {
          return null;
        });
    }
    return productRequests[handle];
  }

  // Only Shopify's CDN understands the width parameter; Judge.me's CDN serves
  // pre-sized files and a stray query string just busts its cache.
  function sizedImageUrl(url, width) {
    var absolute = url.indexOf('//') === 0 ? window.location.protocol + url : url;
    if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(absolute) || /[?&]width=/.test(absolute)) {
      return absolute;
    }
    return absolute + (absolute.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // Reviews synced from another storefront link off-domain, and Shopify's
  // .js endpoint is not CORS-enabled — but Judge.me marks the product shot
  // with .jdgm-rev__product-picture, so prefer that and skip the request
  function pictureFromReview(wrapper) {
    var review = wrapper.closest ? wrapper.closest('.jdgm-rev') : null;
    if (!review) return '';

    var image = review.querySelector('.jdgm-rev__product-picture img');
    if (!image) return '';

    return image.getAttribute('data-src') || image.getAttribute('src') || '';
  }

  function insertProductImage(wrapper, link, url) {
    if (!url || wrapper.querySelector('.hp-jdgm-rev__prod-img')) return;

    var image = document.createElement('img');
    image.className = 'hp-jdgm-rev__prod-img';
    image.setAttribute('loading', 'lazy');
    image.setAttribute('decoding', 'async');
    image.setAttribute('alt', '');
    // Matches the rendered size in homepage-static.css, so the image reserves
    // exactly the box it will occupy and adds no layout shift.
    image.width = 40;
    image.height = 40;
    image.src = sizedImageUrl(url, 80);

    var imageLink = document.createElement('a');
    imageLink.className = 'hp-jdgm-rev__prod-img-link';
    imageLink.setAttribute('href', link.getAttribute('href') || '');
    imageLink.setAttribute('target', '_blank');
    imageLink.setAttribute('rel', 'noopener');
    imageLink.setAttribute('tabindex', '-1');
    imageLink.setAttribute('aria-hidden', 'true');
    imageLink.appendChild(image);

    wrapper.insertBefore(imageLink, wrapper.firstChild);
  }

  function addProductImage(wrapper) {
    if (wrapper.dataset.hpProdImg === 'done') return;
    wrapper.dataset.hpProdImg = 'done';

    var link = wrapper.querySelector('.jdgm-rev__prod-link');
    if (!link) return;

    var pictureUrl = pictureFromReview(wrapper);
    if (pictureUrl) {
      insertProductImage(wrapper, link, pictureUrl);
      return;
    }

    var handle = handleFromLink(link);
    if (!handle) return;

    fetchProduct(handle).then(function (product) {
      if (!product || !product.featured_image) return;
      insertProductImage(wrapper, link, product.featured_image);
    });
  }

  // One observer for the whole page, reused by every section instance.
  function getProductImageObserver() {
    if (!productImageObserver) {
      productImageObserver = new IntersectionObserver(
        function (entries, observer) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            addProductImage(entry.target);
          });
        },
        { rootMargin: '300px' }
      );
    }
    return productImageObserver;
  }

  function loadProductImages(container) {
    if (container.getAttribute('data-product-images') !== 'true') return;

    var wrappers = container.querySelectorAll('.jdgm-rev__prod-info-wrapper:not([data-hp-prod-img])');

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(wrappers, addProductImage);
      return;
    }

    var observer = getProductImageObserver();
    Array.prototype.forEach.call(wrappers, function (wrapper) {
      observer.observe(wrapper);
    });
  }

  // Runs on the injected cards, not at page load: there is no review markup to
  // hydrate until the fetch resolves.
  function hydrate(container) {
    resolveLazyImages(container);
    loadProductImages(container);
  }

  /* ------------------------------------------------------------------- boot */

  function initContainer(container) {
    // The single guard against a second fetch for this container, whichever
    // route got here: the observer firing again on the way back up, a stray
    // init call, or an editor re-render racing the observer.
    if (container.dataset.hpJdgmStarted === 'true') return;
    container.dataset.hpJdgmStarted = 'true';

    var settings = readSettings(container);

    if (!settings.shopDomain || !settings.apiToken) {
      failGracefully(container, 'Set the Judge.me public token and shop domain in the section settings.');
      return;
    }

    var key = cacheKey(settings);
    var servedFromCache = Boolean(reviewRequests[key]) || Boolean(readCache(key));

    loadReviews(settings, function (partial) {
      renderReviews(container, partial, settings);
    })
      .then(function (payload) {
        if (!payload.reviews.length) {
          failGracefully(container, 'Judge.me returned no reviews matching the current rating filter.');
          return;
        }

        finishRender(container, payload, settings);

        if (window.Shopify && window.Shopify.designMode) {
          payload.fromCache = servedFromCache;
          renderIndex(container, payload, settings);
        }
      })
      .catch(function (error) {
        var reason = error && error.timeout
          ? 'Judge.me did not respond in time.'
          : 'Could not load reviews from Judge.me: ' + error.message;
        failGracefully(container, reason);
      });
  }

  function initAll(root) {
    var scope = root || document;
    if (scope.matches && scope.matches(CONTAINER_SELECTOR)) initContainer(scope);
    Array.prototype.forEach.call(scope.querySelectorAll(CONTAINER_SELECTOR), initContainer);
  }

  // The section sits below the fold, so its reviews are only worth fetching once
  // the visitor is heading towards them — a page view that never reaches the
  // section now costs no Judge.me requests at all. Same observer pattern as the
  // product images, with a wider margin so the cards are ready on arrival.
  function getSectionObserver() {
    if (!sectionObserver) {
      sectionObserver = new IntersectionObserver(
        function (entries, observer) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            // Unobserve on the way in so scrolling back up cannot re-enter.
            // initContainer's own guard is the backstop, not the only defence.
            observer.unobserve(entry.target);
            initContainer(entry.target);
          });
        },
        { rootMargin: ROOT_MARGIN }
      );
    }
    return sectionObserver;
  }

  function watchAll(root) {
    var scope = root || document;
    var observer = getSectionObserver();

    Array.prototype.forEach.call(scope.querySelectorAll(CONTAINER_SELECTOR), function (container) {
      if (container.dataset.hpJdgmStarted === 'true') return;
      observer.observe(container);
    });
  }

  // Fallback only. Without an observer there is no way to tell when the section
  // is approaching, so it falls back to the old behaviour — still held until
  // load, so the fetch does not compete with above-the-fold requests.
  function onWindowLoad(run) {
    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true });
    }
  }

  if ('IntersectionObserver' in window) {
    watchAll(document);
  } else {
    onWindowLoad(function () {
      initAll(document);
    });
  }

  // The theme editor swaps in fresh section markup, which arrives with no
  // hpJdgmStarted flag. It is initialised straight away rather than observed:
  // the merchant is looking at the section they just changed, so waiting for an
  // intersection that has probably already happened would just look broken.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target || !target.querySelector || !target.querySelector(CONTAINER_SELECTOR)) return;
    initAll(target);
  });
})();
